import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { MultiSelect } from "../components/ui/multi-select";
import { Combobox } from "../components/ui/combobox";
import { Filter, User, Plus, Archive, Edit, ArrowLeft, Download, RefreshCw, Search, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Settings, ChevronDown, ChevronRight as ChevronRightIcon, LayoutList, Users, BookOpen, CheckCircle2, Clock, AlertCircle, BarChart3, XCircle } from "lucide-react";
import { TrainingPlan, TrainingPlanStatus, User as UserType, Procedure, TrainingPlanTemplate } from "../types";
import { getUsersVisibleTocurrentUser } from "../utils/permissions";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Pagination, PaginationContent, PaginationItem } from "../components/ui/pagination";
import { getMatchedObjects, getUser, toSydneyDate } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { cn } from "../components/ui/utils";

type ViewMode = 'list' | 'by-template';

export default function TrainingPlansPage() {
  const { currentUser } = useAuth();
  const { procedures, users, selectionUsers, addTrainingPlan, trainingPlans, updateTrainingPlan, trainingPlanTemplates } = useData();
  const [personFilter, setPersonFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'archived'>('active');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const navigate = useNavigate();
  const [openArchive, setOpenArchive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [planToArchive, setPlanToArchive] = useState<TrainingPlan | null>(null);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [planToUnarchive, setPlanToUnarchive] = useState<TrainingPlan | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [planToDelete, setPlanToDelete] = useState<TrainingPlan | null>(null);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [ungroupedExpanded, setUngroupedExpanded] = useState(false);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Get visible users based on current user's role
  const visibleUsers = getUsersVisibleTocurrentUser(currentUser, users);

  // Get page title based on user role
  const getPageTitle = () => {
    if (currentUser?.userRole === "Admin" || currentUser?.userRole === "Director") {
      return "Training Plans Management";
    } else if (currentUser?.userRole === "Manager" || currentUser?.userRole === "Senior Manager") {
      return "Team Training Plans";
    } else {
      return "My Training Plans";
    }
  };

  // Get status badge
  const getStatusBadge = (status: TrainingPlanStatus) => {
    let className = "";
    if (status === "In Progress") {
      className = "bg-blue-100 text-blue-800";
    } else if (status === "Completed") {
      className = "bg-green-100 text-green-800";
    } else if (status === "Not Started") {
      className = "bg-gray-200 text-gray-800";
    }
    return (
      <Badge variant="secondary" className={className}>
        {status}
      </Badge>
    );
  };

  const getStatusIcon = (status: TrainingPlanStatus) => {
    if (status === 'Completed') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    if (status === 'In Progress') return <Clock className="w-4 h-4 text-blue-600" />;
    return <AlertCircle className="w-4 h-4 text-gray-400" />;
  };

  // Handle status change
  const handleStatusChange = async (planId: string, newStatus: TrainingPlanStatus) => {
    if (!currentUser) return;

    const plan = trainingPlans.find(p => p.id === planId);
    if (!plan) return;

    // Check permissions for status change
    if (currentUser.userRole === 'Worker') {
      // Workers can only move forward
      if (
        (plan.status === 'Not Started' && newStatus !== 'In Progress') ||
        (plan.status === 'In Progress' && newStatus !== 'Completed')
      ) {
        toast.error("You can only move training forward in status");
        return;
      }
    }

    // Update the plan
    try {
      // TODO: Implement API call to update training plan
      const updatedPlan = { ...plan, status: newStatus };
      if (newStatus === 'In Progress' && !plan.startedOn) {
        updatedPlan.startedOn = new Date().toISOString();
      } else if (newStatus === 'Completed' && !plan.completedOn) {
        updatedPlan.completedOn = new Date().toISOString();
      }

      updateTrainingPlan(planId, updatedPlan);
      toast.success("Training plan status updated");
    } catch (error) {
      toast.error("Failed to update training plan status");
    }
  };

  // Update the filtering logic to handle completed plans
  const filteredTrainingPlans = trainingPlans.filter(plan => {
    // Exclude deleted plans
    if (plan.deleted) return false;

    // Search by plan name
    if (searchTerm && !plan.trainingPlanName?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filter by person
    if (personFilter !== 'all') {
      let traineeId = '';
      if (typeof plan.traineeId === 'string') {
        traineeId = plan.traineeId;
      } else if (typeof plan.traineeId === 'object' && plan.traineeId !== null) {
        traineeId = (plan.traineeId as any).id || (typeof (plan.traineeId as any).toString === 'function' ? (plan.traineeId as any).toString() : '');
      }
      if (String(traineeId) !== String(personFilter)) {
        return false;
      }
    }

    // Filter by status
    if (statusFilter !== 'all' && plan.status !== statusFilter) {
      return false;
    }

    // Handle tab-based filtering
    if (activeTab === 'active') {
      return !plan.archived && plan.status !== 'Completed';
    } else if (activeTab === 'completed') {
      return !plan.archived && plan.status === 'Completed';
    } else if (activeTab === 'archived') {
      return plan.archived;
    }

    return true;
  });

  // Group training plans by source template for admin/director view
  const groupPlansByTemplate = (plans: TrainingPlan[]) => {
    const groups: {
      templateId: string;
      templateName: string;
      jobRoleName?: string;
      plans: TrainingPlan[];
    }[] = [];
    const ungrouped: TrainingPlan[] = [];

    for (const plan of plans) {
      const templateId = plan.sourceTemplateId || plan.sourceTemplate?.id;
      if (templateId && plan.sourceTemplate) {
        const existingGroup = groups.find(g => g.templateId === templateId);
        if (existingGroup) {
          existingGroup.plans.push(plan);
        } else {
          groups.push({
            templateId: templateId,
            templateName: plan.sourceTemplate.templateName || plan.trainingPlanName,
            jobRoleName: plan.sourceTemplate.jobRole?.roleName || plan.sourceTemplate.jobRole?.title,
            plans: [plan]
          });
        }
      } else {
        ungrouped.push(plan);
      }
    }

    return { groups, ungrouped };
  };

  const toggleGroup = (templateId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const isAdminOrDirector = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director";
  const { groups: templateGroups, ungrouped: ungroupedPlans } = isAdminOrDirector && activeTab !== 'archived'
    ? groupPlansByTemplate(filteredTrainingPlans)
    : { groups: [], ungrouped: filteredTrainingPlans };

  // Calculate pagination values
  const totalPages = Math.ceil(filteredTrainingPlans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPlans = filteredTrainingPlans.slice(startIndex, endIndex);

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedGroups(new Set());
  }, [personFilter, statusFilter, searchTerm, activeTab]);

  const handleArchive = async () => {
    if (!planToArchive) return;
    await updateTrainingPlan(planToArchive.id, {
      archived: true,
      archivedOn: new Date().toISOString(),
      archivedBy: currentUser?.id,
      archivedReason: archiveReason || undefined,
    });
    setOpenArchive(false);
    setPlanToArchive(null);
    setArchiveReason("");
  };

  const handleUnarchive = async () => {
    if (!planToUnarchive) return;
    await updateTrainingPlan(planToUnarchive.id, {
      archived: false,
      archivedOn: null,
      archivedBy: null,
    });
    setOpenUnarchive(false);
    setPlanToUnarchive(null);
  };

  const handleDelete = async () => {
    if (!planToDelete || deleteConfirmation !== "delete") return;
    await updateTrainingPlan(planToDelete.id, {
      deleted: true,
      deletedOn: new Date().toISOString(),
      deletedBy: currentUser?.id,
    }, false, true);
    setOpenDelete(false);
    setPlanToDelete(null);
    setDeleteConfirmation("");

    toast.success("Training plan deleted successfully");
  };

  // Calculate completion stats for a group
  const getGroupStats = (plans: TrainingPlan[]) => {
    const total = plans.length;
    const completed = plans.filter(p => p.status === 'Completed').length;
    const inProgress = plans.filter(p => p.status === 'In Progress').length;
    const notStarted = plans.filter(p => p.status === 'Not Started').length;
    const percentComplete = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, notStarted, percentComplete };
  };

  // Render the "By Template" view (Admin/Director only)
  const renderByTemplateView = () => {
    if (templateGroups.length === 0 && ungroupedPlans.length === 0) {
      return (
        <div className="text-center py-16 text-muted-foreground">
          <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No training plans found</p>
          <p className="text-sm">Try adjusting your filters or create a new training plan template.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Template Groups */}
        {templateGroups.map(group => {
          const stats = getGroupStats(group.plans);
          const isExpanded = expandedGroups.has(group.templateId);

          return (
            <Card key={group.templateId} className="overflow-hidden">
              {/* Template Header */}
              <div
                className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleGroup(group.templateId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRightIcon className="h-5 w-5" />}
                    </Button>
                    <div>
                      <h3 className="font-semibold text-lg">{group.templateName}</h3>
                      {group.jobRoleName && (
                        <p className="text-sm text-muted-foreground">Job Role: {group.jobRoleName}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Progress Bar */}
                    <div className="hidden md:flex items-center gap-2">
                      <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${stats.percentComplete}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">
                        {stats.percentComplete}%
                      </span>
                    </div>
                    {/* Stats Badges */}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {stats.total}
                      </Badge>
                      <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {stats.completed}
                      </Badge>
                      {stats.inProgress > 0 && (
                        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {stats.inProgress}
                        </Badge>
                      )}
                      {stats.notStarted > 0 && (
                        <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {stats.notStarted}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Employee List */}
              {isExpanded && (
                <div className="border-t">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/20">
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.plans.map(plan => {
                        const trainee = plan.trainee;
                        return (
                          <TableRow key={plan.id}>
                            <TableCell className="w-8">
                              {getStatusIcon(plan.status)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div>
                                  <Link to={`/people/${trainee?.id}`} className="font-medium hover:underline">
                                    {trainee?.name || "Unknown"}
                                  </Link>
                                  <p className="text-xs text-muted-foreground">{trainee?.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(plan.status)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{toSydneyDate(plan.createdAt)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{plan.startedOn ? toSydneyDate(plan.startedOn) : '-'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{plan.completedOn ? toSydneyDate(plan.completedOn) : '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Link to={`/training-plans/${plan.id}`}>
                                  <Button variant="outline" size="icon" title="View">
                                    <BookOpen className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Can I={ACTIONS.UPDATE} a={RESOURCES.TRAINING_PLAN}>
                                  <Link
                                    to={`/training-plans/${plan.id}/edit`}
                                    className={plan.status === "Completed" ? "pointer-events-none" : ""}
                                    tabIndex={plan.status === "Completed" ? -1 : undefined}
                                  >
                                    <Button variant="outline" size="icon" disabled={plan.status === "Completed"}><Edit className="w-4 h-4" /></Button>
                                  </Link>
                                </Can>
                                <Can I={ACTIONS.ARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                                  <Button variant="outline" size="icon" onClick={() => { setPlanToArchive(plan); setOpenArchive(true); }}><Archive className="w-4 h-4" /></Button>
                                </Can>
                                <Can I={ACTIONS.DELETE} a={RESOURCES.TRAINING_PLAN}>
                                  <Button variant="outline" size="icon" disabled={plan.status === "Completed"} className="text-destructive" onClick={() => { setPlanToDelete(plan); setOpenDelete(true); }}><Trash2 className="w-4 h-4" /></Button>
                                </Can>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          );
        })}

        {/* Ungrouped Plans */}
        {ungroupedPlans.length > 0 && (
          <Card>
            <CardHeader
              className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => setUngroupedExpanded(!ungroupedExpanded)}
            >
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  {ungroupedExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                </Button>
                <LayoutList className="w-4 h-4" />
                Individual Training Plans (Not from Template)
                <Badge variant="secondary" className="ml-2 text-xs">{ungroupedPlans.length}</Badge>
              </CardTitle>
            </CardHeader>
            {ungroupedExpanded && (
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Plan Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ungroupedPlans.map(plan => {
                      const trainee = plan.trainee;
                      return (
                        <TableRow key={plan.id}>
                          <TableCell>
                            <Link to={`/people/${trainee?.id}`} className="hover:underline">{trainee?.name || "Unknown"}</Link>
                          </TableCell>
                          <TableCell>
                            <Link to={`/training-plans/${plan.id}`} className="text-primary hover:underline">{plan.trainingPlanName}</Link>
                          </TableCell>
                          <TableCell>{getStatusBadge(plan.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{toSydneyDate(plan.createdAt)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{plan.startedOn ? toSydneyDate(plan.startedOn) : '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{plan.completedOn ? toSydneyDate(plan.completedOn) : '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Link to={`/training-plans/${plan.id}`}>
                                <Button variant="outline" size="icon" title="View">
                                  <BookOpen className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Can I={ACTIONS.UPDATE} a={RESOURCES.TRAINING_PLAN}>
                                <Link
                                  to={`/training-plans/${plan.id}/edit`}
                                  className={plan.status === "Completed" ? "pointer-events-none" : ""}
                                  tabIndex={plan.status === "Completed" ? -1 : undefined}
                                >
                                  <Button variant="outline" size="icon" disabled={plan.status === "Completed"}><Edit className="w-4 h-4" /></Button>
                                </Link>
                              </Can>
                              <Can I={ACTIONS.ARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                                <Button variant="outline" size="icon" onClick={() => { setPlanToArchive(plan); setOpenArchive(true); }}><Archive className="w-4 h-4" /></Button>
                              </Can>
                              <Can I={ACTIONS.DELETE} a={RESOURCES.TRAINING_PLAN}>
                                <Button variant="outline" size="icon" disabled={plan.status === "Completed"} className="text-destructive" onClick={() => { setPlanToDelete(plan); setOpenDelete(true); }}><Trash2 className="w-4 h-4" /></Button>
                              </Can>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">{getPageTitle()}</h1>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Can I={ACTIONS.CREATE} a={RESOURCES.TRAINING_PLAN}>
            <Link to="/training-plans/new" className="w-full md:w-auto">
              <Button className="w-full md:w-auto flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" />
                <span>Add Training Plan</span>
              </Button>
            </Link>
          </Can>
          <Can I={ACTIONS.READ} a={RESOURCES.TRAINING_PLAN_SETTINGS}>
            <Link to="/training-plans/settings">
              <Button variant="outline" size="icon" title="Settings">
                <Settings className="w-4 h-4" />
              </Button>
            </Link>
          </Can>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={activeTab} className="w-full" onValueChange={v => setActiveTab(v as 'active' | 'completed' | 'archived')}>
        <div className="w-full overflow-x-auto">
          <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
            <TabsTrigger value="active" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Active</TabsTrigger>
            <TabsTrigger value="completed" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Completed</TabsTrigger>
            <TabsTrigger value="archived" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Archived</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Filters & View Toggle */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search training plans..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <Select value={personFilter} onValueChange={setPersonFilter}>
              <SelectTrigger><SelectValue placeholder="Filter by Person" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All People</SelectItem>
                {visibleUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name} ({user.jobRole})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Filter by Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Not Started">Not Started</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* View Mode Toggle - Admin/Director only, not on archived tab */}
        {isAdminOrDirector && activeTab !== 'archived' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-md"
                onClick={() => setViewMode('list')}
              >
                <LayoutList className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === 'by-template' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-md"
                onClick={() => setViewMode('by-template')}
              >
                <BarChart3 className="w-4 h-4 mr-1" />
                By Template
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {isAdminOrDirector && activeTab !== 'archived' && viewMode === 'by-template' ? (
        renderByTemplateView()
      ) : (
        <>
          {/* Mobile View: List of Cards */}
          <div className="md:hidden space-y-4">
            {currentPlans.length > 0 ? (
              currentPlans.map(plan => {
                const trainee = plan.trainee;
                return (
                  <Card key={plan.id} className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-grow min-w-0">
                          <Link to={`/training-plans/${plan.id}`} className="font-semibold text-primary hover:underline block truncate">{plan.trainingPlanName}</Link>
                          {currentUser?.userRole !== "Worker" && (
                            <p className="text-sm text-muted-foreground truncate">Trainee: {trainee?.name || "Unknown"}</p>
                          )}
                        </div>
                        {getStatusBadge(plan.status)}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                        <p><strong>Created:</strong> {toSydneyDate(plan.createdAt)}</p>
                        <p><strong>Started:</strong> {plan.startedOn ? toSydneyDate(plan.startedOn) : 'N/A'}</p>
                        <p><strong>Completed:</strong> {plan.completedOn ? toSydneyDate(plan.completedOn) : 'N/A'}</p>
                      </div>
                      {/* Mobile Actions */}
                      <div className="flex justify-end gap-2 pt-2 border-t">
                        {activeTab === 'archived' ? (
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => { setPlanToUnarchive(plan); setOpenUnarchive(true); }}>Unarchive</Button>
                        ) : (
                          <>
                            <Link
                              to={`/training-plans/${plan.id}/edit`}
                              className={cn("flex-1", plan.status === "Completed" && "pointer-events-none")}
                              tabIndex={plan.status === "Completed" ? -1 : undefined}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                disabled={plan.status === "Completed"}
                              >
                                Edit
                              </Button>
                            </Link>
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => { setPlanToArchive(plan); setOpenArchive(true); }}>Archive</Button>
                            <Button variant="destructive" size="sm" className="flex-1" disabled={plan.status === "Completed"} onClick={() => { setPlanToDelete(plan); setOpenDelete(true); }}>Delete</Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            ) : (
              <p className="text-center text-muted-foreground py-8">No training plans found.</p>
            )}
          </div>

          {/* Desktop View: Table */}
          <Card className="hidden md:block overflow-x-auto">
            <CardContent className="p-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    {currentUser?.userRole !== "Worker" && <TableHead>Trainee</TableHead>}
                    <TableHead>Plan Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created On</TableHead>
                    <TableHead>Started On</TableHead>
                    <TableHead>Completed On</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPlans.length > 0 ? (
                    currentPlans.map(plan => {
                      const trainee = plan.trainee;
                      return (
                        <TableRow key={plan.id}>
                          {currentUser?.userRole !== "Worker" && (
                            <TableCell><Link to={`/people/${trainee?.id}`} className="hover:underline">{trainee?.name || "Unknown"}</Link></TableCell>
                          )}
                          <TableCell><Link to={`/training-plans/${plan.id}`} className="text-primary hover:underline">{plan.trainingPlanName}</Link></TableCell>
                          <TableCell>{getStatusBadge(plan.status)}</TableCell>
                          <TableCell>{toSydneyDate(plan.createdAt)}</TableCell>
                          <TableCell>{plan.startedOn ? toSydneyDate(plan.startedOn) : '-'}</TableCell>
                          <TableCell>{plan.completedOn ? toSydneyDate(plan.completedOn) : '-'}</TableCell>
                          <TableCell className="text-right">
                            {activeTab === 'archived' ? (
                              <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                                <Button variant="outline" size="icon" onClick={() => { setPlanToUnarchive(plan); setOpenUnarchive(true); }}><RefreshCw className="w-4 h-4" /></Button>
                              </Can>
                            ) : (
                              <div className="flex justify-end gap-2">
                                <Can I={ACTIONS.UPDATE} a={RESOURCES.TRAINING_PLAN}>
                                  <Link
                                    to={`/training-plans/${plan.id}/edit`}
                                    className={plan.status === "Completed" ? "pointer-events-none" : ""}
                                    tabIndex={plan.status === "Completed" ? -1 : undefined}
                                  >
                                    <Button variant="outline" size="icon" disabled={plan.status === "Completed"}><Edit className="w-4 h-4" /></Button>
                                  </Link>
                                </Can>

                                <Can I={ACTIONS.ARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                                  <Button variant="outline" size="icon" onClick={() => { setPlanToArchive(plan); setOpenArchive(true); }}><Archive className="w-4 h-4" /></Button>
                                </Can>

                                <Can I={ACTIONS.DELETE} a={RESOURCES.TRAINING_PLAN}>
                                  <Button variant="outline" size="icon" disabled={plan.status === "Completed"} className="text-destructive" onClick={() => { setPlanToDelete(plan); setOpenDelete(true); }}><Trash2 className="w-4 h-4" /></Button>
                                </Can>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={currentUser?.userRole !== "Worker" ? 7 : 6} className="text-center h-24">No training plans found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Pagination */}
      {filteredTrainingPlans.length > itemsPerPage && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></Button></PaginationItem>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button></PaginationItem>
              {getPageNumbers().map((pageNum) => (
                <PaginationItem key={pageNum}><Button variant={currentPage === pageNum ? "outline" : "ghost"} onClick={() => handlePageChange(pageNum)} className="size-9">{pageNum}</Button></PaginationItem>
              ))}
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button></PaginationItem>
              <PaginationItem><Button variant="ghost" size="icon" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="h-4 w-4" /></Button></PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Archive Training Plan Dialog */}
      <Dialog open={openArchive} onOpenChange={setOpenArchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Training Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this training plan? You can unarchive it later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="archiveReason">Reason for archiving (optional)</Label>
              <Textarea
                id="archiveReason"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleArchive();
                  }
                }}
                placeholder="Enter reason for archiving this training plan..."
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenArchive(false)}>
              Cancel
            </Button>
            <Button onClick={handleArchive} className="bg-black text-white hover:bg-gray-800">
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Training Plan Dialog */}
      <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unarchive Training Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to unarchive this training plan? It will become active again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUnarchive(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnarchive}>
              Unarchive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Training Plan Dialog */}
      <Dialog open={openDelete} onOpenChange={(open) => {
        setOpenDelete(open);
        if (open) setDeleteConfirmation("");
        if (!open) {
          setPlanToDelete(null);
          setDeleteConfirmation("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Training Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this training plan? Type the word "delete" below to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="deleteConfirmation">Confirmation</Label>
              <Input
                id="deleteConfirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && deleteConfirmation === "delete") {
                    e.preventDefault();
                    handleDelete();
                  }
                }}
                placeholder="delete"
                className="bg-gray-200"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setOpenDelete(false);
              setPlanToDelete(null);
              setDeleteConfirmation("");
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteConfirmation !== "delete"}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
